import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  ReceiptBatchMangopayPaymentCard,
  ReceiptBatchPaymentCard,
  ReceiptBatchSummaryCardDetailed,
} from '@pflegenavi/shared-web/receipt-batch';
import {
  MangopayPaymentStatus,
  useCancelReceiptBatchEntryPhoenix,
  useGetDashboardPhoenix,
  useGetReceiptBatchPaymentPhoenix,
  useGetReceiptImagesWithTenant,
  useGetServiceProviderPhoenix,
  useServiceProvider,
  useServiceProviderReceiptBatchPayment,
} from '@pflegenavi/service-provider-frontend/api';
import { ClientError } from '@pflegenavi/shared-frontend/platform';
import type { ReceiptBatchState } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ReceiptBatchPaymentState } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ServiceProviderReceiptBatchDetails } from './form/ServiceProviderReceiptBatchDetails';
import type { BatchReceiptDetails } from './form/ServiceProviderReceiptBatchForm';
import { ServiceProviderStripeOperatedBy } from '@pflegenavi/shared/api';
import { useGetGlobalServiceProviderId } from '../../hooks/useGetGlobalServiceProviderId';
import { CancelReceiptBatchEntryDialog } from './CancelReceiptBatchEntryDialog';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

interface ReceiptBatchDetailsContainerProps {
  id: string | undefined;
  isLoading: boolean;
  receipts: BatchReceiptDetails[];
  autoGenerateReceipts?: boolean;
  generateReceiptBatchPreview?: () => Promise<Blob | undefined>;
  autoGenerateReceiptsPreview?: (entryId: string) => Promise<Blob | undefined>;
  totalAmount: number;
  receiptsCount: number;
  batchImageIds?: string[];
  receiptDate?: Date;
  nursingHomeName?: string;
  tenant?: string;
  state?: ReceiptBatchState;
  lastUpdateDate?: Date;
  useIndividualDates: boolean;
  minReceiptDate?: Date;
  maxReceiptDate?: Date;
}

interface CancelDialogEntry {
  entryId: string;
  residentName: string;
  amount: number;
  embargoDate?: Date;
}

export const ReceiptBatchDetailsContainer: FC<
  ReceiptBatchDetailsContainerProps
> = ({
  id,
  isLoading,
  receipts,
  autoGenerateReceipts,
  autoGenerateReceiptsPreview,
  tenant,
  batchImageIds,
  nursingHomeName,
  receiptsCount,
  totalAmount,
  receiptDate,
  state,
  lastUpdateDate,
  maxReceiptDate,
  minReceiptDate,
  useIndividualDates,
  generateReceiptBatchPreview,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const useGetReceiptImages = useGetReceiptImagesWithTenant({
    tenant: tenant ?? '',
  });

  const globalServiceProviderId =
    useGetGlobalServiceProviderId().globalServiceProviderId;

  const { data: company } = useServiceProvider({
    globalServiceProviderId: globalServiceProviderId ?? '',
  });

  const { data: paymentStatus } = useServiceProviderReceiptBatchPayment({
    receiptBatchId: id,
  });

  const { data: serviceProviderProfile } = useGetServiceProviderPhoenix();

  const isMangopay = useMemo(
    () => !!serviceProviderProfile?.data.mangopay_user_id,
    [serviceProviderProfile?.data.mangopay_user_id]
  );

  const { data: paymentPhoenix } = useGetReceiptBatchPaymentPhoenix({
    receiptBatchId: id,
    options: {
      enabled: isMangopay,
    },
  });

  const { data: dashboard } = useGetDashboardPhoenix();

  const cancelMutation = useCancelReceiptBatchEntryPhoenix();

  const [cancelDialogEntry, setCancelDialogEntry] =
    useState<CancelDialogEntry | null>(null);

  const handleCancelEntry = useCallback(
    (entryId: string) => {
      const receipt = receipts.find((r) => r.id === entryId);
      const paymentEntry =
        paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries.find(
          (e) => e.receipt_batch_entry_id === entryId
        );

      if (!receipt || !paymentEntry) {
        return;
      }

      const amount = receipt.lineItems.reduce(
        (acc, item) => acc + (item.amount ?? 0),
        0
      );

      setCancelDialogEntry({
        entryId,
        residentName: receipt.residentName ?? '--',
        amount,
        embargoDate: paymentEntry.embargo_date
          ? new Date(paymentEntry.embargo_date)
          : undefined,
      });
    },
    [receipts, paymentPhoenix]
  );

  const handleCancelConfirm = useCallback(
    async (note: string) => {
      if (!cancelDialogEntry || !id) {
        return;
      }
      try {
        await cancelMutation.mutateAsync({
          batchId: id,
          entryId: cancelDialogEntry.entryId,
          note,
        });
        enqueueSnackbar(t('receipts.batch.entry.cancel-success'), {
          variant: 'success',
        });
        setCancelDialogEntry(null);
      } catch (e) {
        if (e instanceof ClientError) {
          // @ts-expect-error errors untyped on ClientError
          const detail = e.errors?.detail as string | undefined;
          if (detail?.includes('already cancelled')) {
            enqueueSnackbar(
              t('receipts.batch.entry.cancel-error-already-cancelled'),
              { variant: 'error' }
            );
          } else if (detail?.includes('not been paid')) {
            enqueueSnackbar(t('receipts.batch.entry.cancel-error-not-paid'), {
              variant: 'error',
            });
          } else {
            enqueueSnackbar(t('errors.something-went-wrong'), {
              variant: 'error',
            });
          }
        } else {
          enqueueSnackbar(t('errors.something-went-wrong'), {
            variant: 'error',
          });
        }
        setCancelDialogEntry(null);
      }
    },
    [cancelDialogEntry, id, cancelMutation, enqueueSnackbar, t]
  );

  const isLateCancellation = useMemo(() => {
    if (!cancelDialogEntry?.embargoDate) {
      return false;
    }
    return cancelDialogEntry.embargoDate < new Date();
  }, [cancelDialogEntry]);

  const mangopayPaidAmount = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries
      .filter((entry) => entry.status === MangopayPaymentStatus.Paid)
      .reduce((acc, entry) => acc + entry.amount, 0);
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const mangopayPaidAdditionalFeeAmount = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries
      .filter((entry) => entry.status === MangopayPaymentStatus.Paid)
      .reduce((acc, entry) => acc + (entry.additional_fee_amount ?? 0), 0);
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const mangopayUnpaidReceipts = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries.filter(
      (entry) => entry.status === MangopayPaymentStatus.Pending
    );
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const mangopayCancelledReceipts = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries.filter(
      (entry) => entry.cancellation != null
    );
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const mangopayDeletedReceipts = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries.filter(
      (entry) => entry.status === MangopayPaymentStatus.ReceiptDeleted
    );
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const receiptBatchPaymentCard = useMemo(() => {
    if (isMangopay && paymentPhoenix?.data) {
      return (
        <ReceiptBatchMangopayPaymentCard
          isLoading={false}
          nursingHomeName={nursingHomeName}
          paidAmountInCents={mangopayPaidAmount ?? 0}
          paidAdditionalFeeAmountInCents={mangopayPaidAdditionalFeeAmount ?? 0}
          unpaidAmountInCents={(mangopayUnpaidReceipts ?? []).reduce(
            (acc, entry) => acc + entry.amount,
            0
          )}
          unpaidAdditionalFeeAmountInCents={(
            mangopayUnpaidReceipts ?? []
          ).reduce((acc, entry) => acc + (entry.additional_fee_amount ?? 0), 0)}
          unpaidCount={mangopayUnpaidReceipts?.length ?? 0}
          cancelledAmountInCents={(mangopayCancelledReceipts ?? []).reduce(
            (acc, entry) => acc + entry.amount,
            0
          )}
          cancelledCount={mangopayCancelledReceipts?.length ?? 0}
          deletedAmountInCents={(mangopayDeletedReceipts ?? []).reduce(
            (acc, entry) => acc + entry.amount,
            0
          )}
          deletedCount={mangopayDeletedReceipts?.length ?? 0}
        />
      );
    }
    if (
      company?.stripeOperatedBy !==
        ServiceProviderStripeOperatedBy.Pflegenavi &&
      paymentStatus?.status === ReceiptBatchPaymentState.PAYMENT_INITIATED
    ) {
      return (
        <ReceiptBatchPaymentCard
          isLoading={false}
          nursingHomeName={paymentStatus.payment.nursingHome.name}
          payment={{
            id: paymentStatus.payment.id,
            amountInCents: paymentStatus.payment.amount,
            completedAt: paymentStatus.payment.completedAt ?? undefined,
            initiationDate: paymentStatus.payment.created,
            status: paymentStatus.payment.status,
          }}
        />
      );
    }
    return undefined;
  }, [
    mangopayPaidAdditionalFeeAmount,
    company?.stripeOperatedBy,
    isMangopay,
    mangopayPaidAmount,
    mangopayUnpaidReceipts,
    mangopayCancelledReceipts,
    mangopayDeletedReceipts,
    nursingHomeName,
    paymentStatus,
    paymentPhoenix?.data,
  ]);

  if (!tenant || !nursingHomeName || !company) {
    return null;
  }

  return (
    <>
      <ServiceProviderReceiptBatchDetails
        isLoading={isLoading}
        receipts={receipts}
        payment={paymentPhoenix}
        autoGenerateReceipts={autoGenerateReceipts}
        autoGenerateReceiptsPreview={autoGenerateReceiptsPreview}
        useReceiptImages={useGetReceiptImages}
        width={330}
        receiptDate={receiptDate}
        useIndividualDates={useIndividualDates}
        onCancelEntry={isMangopay ? handleCancelEntry : undefined}
        ReceiptBatchSummaryCard={
          <ReceiptBatchSummaryCardDetailed
            useReceiptImages={useGetReceiptImages}
            title={nursingHomeName ?? ''}
            receiptDate={new Date(receiptDate ?? Date.now())}
            useIndividualDates={useIndividualDates}
            minReceiptDate={minReceiptDate}
            maxReceiptDate={maxReceiptDate}
            totalAmount={totalAmount}
            totalAdditionalFeeAmount={0}
            totalIncludedFeeAmount={0}
            receiptsCount={receiptsCount}
            receiptImageIds={batchImageIds}
            lastUpdateDate={lastUpdateDate}
            state={state ?? 'draft'}
            autoGenerateReceipts={autoGenerateReceipts}
            generateReceiptBatchPreview={generateReceiptBatchPreview}
          />
        }
        ReceiptBatchPaymentCard={receiptBatchPaymentCard}
      />
      {cancelDialogEntry && (
        <CancelReceiptBatchEntryDialog
          open={true}
          onClose={() => setCancelDialogEntry(null)}
          onConfirm={handleCancelConfirm}
          entryAmount={cancelDialogEntry.amount}
          residentName={cancelDialogEntry.residentName}
          isLateCancellation={isLateCancellation}
          availableBalance={dashboard?.available_balance}
        />
      )}
    </>
  );
};
